import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from './anchors'
import { SHARED_ANCHORS } from '../shared/anchors'
import { withScopes } from '../shared/scopes'
import type { IOnboardingScenario } from '../../types/IOnboardingScenario'
import type { IOnboardingScope } from '../../types/IOnboardingScope'
import type { IOnboardingStep } from '../../types/IOnboardingStep'

const { toolbar, register, card, process, table, chart } = AUDITS_ANCHORS
const ctx = AUDITS_CONTEXTS

/* navPanel — раскрытая «Навигация по разделам» на телефоне (на остальных ширинах действие ничего не делает) */
const navPanelScope: IOnboardingScope = { open: { context: ctx.toolbar, action: 'openNavPanel' } }

const cardSteps: IOnboardingStep[] = [
  { id: 'audits.card.start', anchors: [card.start], context: ctx.card },
  { id: 'audits.card.downloads', anchors: [card.downloads], context: ctx.card },
  { id: 'audits.card.nonconformities', anchors: [card.nonconformities], context: ctx.card },
  { id: 'audits.card.checklist', anchors: [card.checklist], context: ctx.card },
  { id: 'audits.card.edit', anchors: [card.edit], context: ctx.card },
  { id: 'audits.card.departments', anchors: [card.departments], context: ctx.card },
  { id: 'audits.card.assignments', anchors: [card.assignments], context: ctx.card },
  { id: 'toolbar.onboarding', anchors: [SHARED_ANCHORS.toolbar.onboarding], context: ctx.card },
]

/*
 * Проведение проверки — отдельная страница, открывается из карточки на учебном аудите.
 * Шаги идут последними: возврат со страницы проверки снова монтирует карточку.
 */
const processScope: IOnboardingScope = {
  open: { context: ctx.card, action: 'openDemoProcess' },
  close: { context: ctx.process, action: 'backToCard' },
}

const processSteps: IOnboardingStep[] = [
  { id: 'audits.process.intro', anchors: [], context: ctx.process },
  { id: 'audits.process.criteria', anchors: [process.criteria], context: ctx.process, placement: 'bottom' },
  { id: 'audits.process.questions', anchors: [process.questions], context: ctx.process, placement: 'right' },
  { id: 'audits.process.comment', anchors: [process.comment], context: ctx.process, placement: 'right' },
  { id: 'audits.process.decision', anchors: [process.decision], context: ctx.process, placement: 'left' },
]

/**
 * Шаги таблиц подразделов: поиск, сброс фильтров и сама таблица с фильтрами колонок.
 * Текст шага таблицы свой у раздела — клик по строке открывает запись не везде.
 * Таблица во всю высоту экрана, поэтому карточку шага прижимаем вниз, чтобы не закрыть заголовки с фильтрами.
 * `afterIntro` — шаги раздела, которые идут сразу за вступлением (например, кнопка создания).
 */
function sectionTableSteps(prefix: string, context: string, afterIntro: IOnboardingStep[] = []): IOnboardingStep[] {
  return [
    { id: `${prefix}.intro`, anchors: [], context },
    ...afterIntro,
    { id: 'audits.table.search', anchors: [table.search], context },
    { id: `${prefix}.table`, anchors: [table.content], context, placement: 'bottom' },
    { id: 'audits.table.resetFilters', anchors: [table.resetFilters], context },
  ]
}

export const auditsScenarios: IOnboardingScenario[] = [
  {
    id: 'audits',
    section: 'audit',
    isMain: true,
    scopes: {
      navPanel: navPanelScope,
      filtersPanel: { open: { context: ctx.register, action: 'openFiltersPanel' } },
      card: {
        open: { context: ctx.register, action: 'openDemoCard' },
        close: { context: ctx.register, action: 'closeCard' },
      },
      process: processScope,
    },
    steps: [
      { id: 'audits.intro', anchors: [], context: ctx.register },
      { id: 'audits.createAudit', anchors: [toolbar.createAudit], context: ctx.toolbar },
      ...withScopes(['navPanel'], [
        { id: 'audits.nav', anchors: [toolbar.navFirst, toolbar.navLast], context: ctx.toolbar },
        { id: 'toolbar.reports', anchors: [SHARED_ANCHORS.toolbar.reports], context: ctx.toolbar },
        { id: 'toolbar.onboarding', anchors: [SHARED_ANCHORS.toolbar.onboarding], context: ctx.toolbar },
      ]),
      ...withScopes(['filtersPanel'], [
        { id: 'audits.register.search', anchors: [register.search], context: ctx.register },
        { id: 'audits.register.flags', anchors: [register.overdue, register.requiresAction], context: ctx.register },
        { id: 'audits.register.resetFilters', anchors: [register.resetFilters], context: ctx.register },
      ]),
      { id: 'audits.register.filters', anchors: [register.filterFirst, register.filterTallest, register.filterLast], context: ctx.register },
      { id: 'audits.register.demoRow', anchors: [register.demoRow], context: ctx.register },
      ...withScopes(['card'], cardSteps.filter(step => step.id !== 'toolbar.onboarding')),
      ...withScopes(['card', 'process'], processSteps),
    ],
  },
  {
    id: 'audits.card',
    section: 'audit',
    isMain: false,
    scopes: {
      card: {
        open: { context: ctx.card, action: 'showDemo' },
        close: { context: ctx.card, action: 'showReal' },
      },
      process: processScope,
    },
    steps: [...withScopes(['card'], cardSteps), ...withScopes(['card', 'process'], processSteps)],
  },
  {
    id: 'audits.nonconformities',
    section: 'audit',
    isMain: false,
    scopes: {},
    steps: sectionTableSteps('audits.nonconformities', ctx.nonconformities),
  },
  {
    id: 'audits.groups',
    section: 'audit',
    isMain: false,
    scopes: {},
    steps: sectionTableSteps('audits.groups', ctx.groups, [
      { id: 'audits.groups.create', anchors: [toolbar.createGroup], context: ctx.toolbar },
    ]),
  },
  {
    id: 'audits.archive',
    section: 'audit',
    isMain: false,
    scopes: {},
    steps: sectionTableSteps('audits.archive', ctx.archive),
  },
  {
    id: 'audits.chart',
    section: 'audit',
    isMain: false,
    scopes: {
      sidebar: { open: { context: ctx.chart, action: 'openSidebar' } },
    },
    steps: [
      { id: 'audits.chart.calendar', anchors: [chart.calendar], context: ctx.chart, placement: 'bottom' },
      ...withScopes(['sidebar'], [
        { id: 'audits.chart.create', anchors: [chart.create], context: ctx.chart, placement: 'right' },
        { id: 'audits.chart.datePicker', anchors: [chart.datePicker], context: ctx.chart, placement: 'right' },
        { id: 'audits.chart.types', anchors: [chart.types], context: ctx.chart, placement: 'right' },
      ]),
    ],
  },
]
